// Copyright (c) Docugami, Inc. All rights reserved.
import 'react-app-polyfill/ie9';
import React from 'react'
import { Provider } from "react-redux";
import ReactDOM from 'react-dom';
import { Router } from 'react-router-dom';
import { createBrowserHistory } from 'history';
import { App } from './components/shell/presentation/App';
import * as serviceWorker from './serviceWorker';

import { ClientConfigurationApi } from './networking/api'
import { Route, Switch } from 'react-router';

import "./components/shell/styles/shell.scss";
import { getDebugLoadDelay } from './utils/localStorageHelpers';
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { AppInsightsPlugin, AppInsightsTelemetryProvider } from 'telemetry/AppInsightsTelemetryProvider';
import { Telemetry } from 'telemetry/TelemetryManager';
import { AALoadAddinSchema, TelemetryEvent } from 'telemetry/TelemetryEvents';
import { RequestScheduler } from 'networking/RequestScheduler';
import { Config } from 'config';
import { Logger, LoggerCategory } from 'utils/Logger';
import { ITelemetryProvider } from 'telemetry/TelemetryProvider';
import { SessionExpiredPage } from 'pages/SessionExpiredPage';
import { Auth0Provider } from '@auth0/auth0-react';
import axios from 'axios';
import { CallbackPage } from 'components/shell/presentation/CallbackPage';
import { Logout, OrgSwitch } from 'components/shell/presentation/Login';
import { WordAuthLoginDialog } from 'components/assisted_authoring/WordAuthLoginDialog';
import { WordAuthLogoutDialog } from 'components/assisted_authoring/WordAuthLogoutDialog';
import { AaClausesPage } from 'pages/AaClausesPage';
import { EmailVerificationPage } from 'pages/EmailVerificationPage';
import { store } from './store';
import { authAccessor } from 'authentication/authAccessor';
import { NotFoundPage } from 'pages/NotFoundPage';
import { GoogleAnalyticsTelemetryProvider } from 'telemetry/GoogleAnalyticsTelemetryProvider';
import { ScheduledMaintenancePage } from 'pages/MaintenancePage';
import { frontendSlice } from 'store/frontend/frontendSlice';
import { sleep } from 'utils/AsyncHelpers';

require('polyfill-object.fromentries');

Logger.config = Config.logger;

export const requestScheduler = new RequestScheduler(axios);

const { detect } = require('detect-browser');
export const browser = detect();

if (browser.name === "ie") {
    ReactDOM.render(<div>
        <div style={{ width: '100%', textAlign: 'center', paddingTop: '10px' }}>We're sorry, but Internet Explorer is not supported by Docugami at this time.</div>
        <div style={{ width: '100%', textAlign: 'center', paddingTop: '10px' }}>If you are visiting from a browser, please consider using Google Chrome or the new Microsoft Edge for best results.</div>
        <div style={{ width: '100%', textAlign: 'center', paddingTop: '10px' }}>If you are attempting to load the Docugami taskpane inside of Microsoft Word, this requires Windows 10 version 1903, or above, and Office 365 version 16.0.11629, or above.</div>
    </div>, document.getElementById('root'));
}
else {
    const history = createBrowserHistory();

    authAccessor.setOrganizationId(() => {
        return store.getState().organizations.currentOrganization?.id ?? ''
    });

    getAppInsightsConnectionString().then(
        async (appInsightsConnectionString: string) => {
            const baseUrl = document.getElementsByTagName('base')[0].getAttribute('href');
            const rootElement = document.getElementById('root');

            function setup(debug: boolean) {
                // give Office 2 seconds to initialize, if it fails continue as not Office
                let timeout = setTimeout(() => render(), 2000);

                const Office = (window as any).Office
                Office.onReady()
                    .then((info: any) => {
                        (window as any)._OfficeMode = info.host !== null;
                        (window as any)._AddInPlatform = info.platform;
                        if (info.host !== null) {
                            Telemetry.trackEvent<AALoadAddinSchema>({ eventName: TelemetryEvent.AALoadAddin });
                            if (debug) {
                                OfficeExtension.config.extendedErrorLogging = true;
                            }
                        }

                        clearTimeout(timeout);
                        render();
                    });
            }

            function render() {

                if (!!appInsightsConnectionString) {
                    const appInsightsTelemetryProvider: ITelemetryProvider = new AppInsightsTelemetryProvider(appInsightsConnectionString);
                    Telemetry.register(appInsightsTelemetryProvider);
                }

                if (!!Config.googleAnalyticsKey) {
                    const googleAnalyticsTelemetryProvider: ITelemetryProvider = new GoogleAnalyticsTelemetryProvider(Config.googleAnalyticsKey);
                    Telemetry.register(googleAnalyticsTelemetryProvider);
                }

                // show the cookie warning 1 second after rendering
                setTimeout(() => setCookieTrackingUXVisibility(), 1000)

                ReactDOM.render(
                    <Provider store={store}>
                        <Auth0Provider
                            domain={Config.auth0.domain}
                            clientId={Config.auth0.clientId}
                            redirectUri={`${Config.baseUrl}/callback`}
                            audience={Config.auth0.audience}
                            scope={Config.auth0.scope}
                            useRefreshTokens={true}
                            cacheLocation="localstorage">
                            <AppInsightsContext.Provider value={AppInsightsPlugin.instance}>
                                <Router
                                    // @ts-ignore
                                    basename={baseUrl}
                                    history={history}>
                                    <Switch>
                                        <Route path="/verify-email" component={EmailVerificationPage} />
                                        <Route path='/session-expired' component={SessionExpiredPage} exact />
                                        <Route path='/callback' component={CallbackPage} />
                                        <Route path='/storage/auth/callback' component={() => {
                                            // This route is no longer used, but redirecting to facilitate transition of existing connections
                                            window.location.replace(`${Config.storageUrl}/connections/oauth/callback${window.location.search}`);
                                            return null;
                                        }}/>
                                        <Route path='/logout' component={Logout} />
                                        <Route path='/org/switch' component={OrgSwitch} />
                                        <Route path={'/word/dialog/auth/login'} component={WordAuthLoginDialog} />
                                        <Route path={'/word/dialog/auth/logout'} component={WordAuthLogoutDialog} />
                                        <Route path='/aaClauses/selectedClause/:selectedClause' component={AaClausesPage} />
                                        <Route path='/page-not-found' component={NotFoundPage} />
                                        <Route path="/" component={App} />
                                    </Switch>
                                </Router>
                            </AppInsightsContext.Provider>
                        </Auth0Provider>
                    </Provider>,
                    rootElement);
            }

            window.history.replaceState = (window as any)._historyCache.replaceState;
            window.history.pushState = (window as any)._historyCache.pushState;

            function setupAppWithDebugDelay() {
                const delay = getDebugLoadDelay();
                if (delay === 0) {
                    setup(false)
                } else {
                    setTimeout(() => setup(true), delay);
                }
            }

            const renderMaintenancePage = () => ReactDOM.render(<Provider store={store}><ScheduledMaintenancePage /></Provider>, rootElement);

            async function checkIfHealthy() {
                const queryResponse = await store.dispatch(frontendSlice.endpoints.health.initiate());
                return queryResponse?.data === true;
            }

            // Check frontend service health before rendering the app
            // If we fail, ensure the frontend service is not healthy by attempting to ping again after 3s,
            // then render the scheduled maintenance page
            let isHealthy = await checkIfHealthy();
            if (!isHealthy) {
                await sleep(3000);
                isHealthy = await checkIfHealthy();
            }

            if (!isHealthy) {
                renderMaintenancePage()
            } else {
                setupAppWithDebugDelay()
            }
        });
}

async function getAppInsightsConnectionString() {
    let appInsightsConnectionString = Config.appInsights.connectionString;
    if (!appInsightsConnectionString) {
        const clientConfigurationApi = new ClientConfigurationApi();
        try {
            const response = await clientConfigurationApi.clientConfigurationConnectionString();
            appInsightsConnectionString = response.data;
        }
        catch (error) {
            Logger.LogError(LoggerCategory.Telemetry, "Failed to retrieve app insights key.");
        }
    }

    return appInsightsConnectionString;
}


function setCookieTrackingUXVisibility(): void {
    if (!(window as any)._OfficeMode) {
        // show the cookie warning when not in office (it is hidden by default)
        let suppressCookieWarningCss = "div.cookie-confirmation-display { display: inline; }";
        var styleElem = document.createElement('style');
        styleElem.appendChild(document.createTextNode(suppressCookieWarningCss));
        document.getElementsByTagName('head')[0].appendChild(styleElem);
    }
}


// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
